import { DASHBOARD_TILE_LABEL } from '@/lib/labels';
import type { DashboardTileId } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  A TILE'S WORDS, WITH THE OPERATOR'S OVERRIDES APPLIED. [P6b]
 *
 *  ⚠️ [amended C1e §3] THERE IS A THIRD PARAMETER KEY NOW, AND THAT REVERSES [P6b] §3c.
 *
 *  This header used to say "two parameters and no third, and the absence IS the enforcement".
 *  The hint — the description line under a tile's name — is overridable on a client decision.
 *  `labels.ts` still says why it was structural: the `hint` is where a NUMBER'S BOUNDARY is
 *  stated ("within your scope only", "command roles are exempt and are not counted", "from the
 *  officer's own handset clock"), and [P6a] wrote those because a fairness metric that does not
 *  state its own boundary becomes an unfairness generator.
 *
 *  ⚠️ WHAT STILL HOLDS, AND IS WHY THE REVERSAL IS AFFORDABLE. The CONFIGURATOR and the tile
 *  LIBRARY read `DASHBOARD_TILE_LABEL[id].hint` **DIRECTLY**, never through this function — so
 *  an operator who hides the line on the card still reads, at rest and beside the field, the
 *  sentence their title has to live with. Do not "tidy" those two call sites onto this
 *  function: the asymmetry IS §3d. The configurator shows what the tile IS; the card shows
 *  what the operator CHOSE.
 *
 *  ⚠️ THREE VALUES, NOT TWO, AND NO BINARY OPERATOR CAN EXPRESS IT.
 *
 *      null  →  the registry's own sentence      (never set)
 *      ''    →  the line is ABSENT entirely      (§3b — not blank, not a gap)
 *      'x'   →  'x'
 *
 *  Which is why `hint` goes through a NAMED FUNCTION rather than `||` or `??`: `||` collapses
 *  `''` to the registry sentence (the opposite of what it means) and a bare `??` would let a
 *  whitespace-only override through as a visible blank line.
 *
 *  ⚠️ THE TWO LABELS KEEP `||`, AND IT IS STILL EXACTLY RIGHT FOR THEM. `??` passes `''`
 *  through, and an empty `<h3>` is a defect nobody can report except as "the tile has no name".
 *  The wire refuses a blank title outright; this is the belt for a cache entry written before
 *  that schema tightened, and for a row edited by anything other than the configurator.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type TileWords = {
  readonly title: string;
  /** ⚠️ `null` MEANS "RENDER NO LINE" — `TileCard` omits the `<p>` rather than emptying it. */
  readonly hint: string | null;
  readonly drill: string;
};

/** What the wire carries per layout entry. Deliberately NOT the whole entry. */
export type TileWordOverrides = {
  readonly titleOverride: string | null;
  readonly drillLabelOverride: string | null;
  /** ⚠️ [C1e §3] The tri-state. See the header. */
  readonly hintOverride: string | null;
};

const resolveHint = (base: string, override: string | null | undefined): string | null => {
  if (override === undefined || override === null) return base;
  const text = override.trim();
  return text.length > 0 ? text : null;
};

export const resolveTileWords = (id: DashboardTileId, overrides?: TileWordOverrides | null): TileWords => {
  const base = DASHBOARD_TILE_LABEL[id];
  return {
    title: overrides?.titleOverride?.trim() || base.title,
    hint: resolveHint(base.hint, overrides?.hintOverride),
    drill: overrides?.drillLabelOverride?.trim() || base.drill,
  };
};
